/**
 * SMS length, encoding and credit estimates (CHN-06, CHN-07, GRD-06).
 *
 *   GSM-7:   1 segment = 160 chars, multi-part = 153 chars per segment
 *   Unicode: 1 segment =  70 chars, multi-part =  67 chars per segment
 *
 * Unicode (UCS-2) applies when any character falls outside the GSM-7 set.
 * Characters in the GSM-7 extension table take two units.
 */
import { expandForEstimate } from './tokens';

/** CHN-06 — body limit after the token expansion estimate. */
export const SMS_BODY_LIMIT = 320;

/** GRD-06 — added to every marketing SMS and counted in the length preview. */
export const SMS_OPT_OUT_SUFFIX = 'Reply STOP to opt out';

const GSM7_BASIC =
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';
const GSM7_EXT = '^{}\\[~]|€';

const basicSet = new Set(Array.from(GSM7_BASIC));
const extSet = new Set(Array.from(GSM7_EXT));

export function isGsm7(text: string): boolean {
  for (const ch of text) {
    if (!basicSet.has(ch) && !extSet.has(ch)) return false;
  }
  return true;
}

function unitLength(text: string, gsm: boolean): number {
  if (!gsm) return text.length;
  let n = 0;
  for (const ch of text) n += extSet.has(ch) ? 2 : 1;
  return n;
}

export function smsPrefix(brandName: string): string {
  return `${brandName}: `;
}

/** GRD-06 — the text as delivered: brand name first, opt-out instruction last. */
export function composeSms(body: string, brandName: string): string {
  return `${smsPrefix(brandName)}${body.trim()} ${SMS_OPT_OUT_SUFFIX}`;
}

export interface SmsAnalysis {
  /** Body length after the token expansion estimate. */
  bodyChars: number;
  /** Full message length in encoding units, including prefix and suffix. */
  totalChars: number;
  encoding: 'GSM-7' | 'Unicode';
  segments: number;
  overLimit: boolean;
}

export function analyzeSms(body: string, brandName: string): SmsAnalysis {
  const expandedBody = expandForEstimate(body);
  const full = composeSms(expandedBody, brandName);
  const gsm = isGsm7(full);
  const totalChars = unitLength(full, gsm);
  const single = gsm ? 160 : 70;
  const multi = gsm ? 153 : 67;
  const segments = body.trim().length === 0 ? 0 : totalChars <= single ? 1 : Math.ceil(totalChars / multi);
  return {
    bodyChars: expandedBody.length,
    totalChars,
    encoding: gsm ? 'GSM-7' : 'Unicode',
    segments,
    overLimit: expandedBody.length > SMS_BODY_LIMIT,
  };
}

/** SND-09 — one credit per segment per message. */
export function estimateCredits(segments: number, smsRecipients: number): number {
  return segments * smsRecipients;
}
